import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Shield, Clock, CheckCircle, ArrowRight, DollarSign, Zap } from 'lucide-react';
import { BrandLogo } from '@/components/ui/brand-logo';
import { trackRedditViewContent } from '@/analytics/reddit';
import { trackEvent } from '@/analytics/ga';

export const MobileLandingPage = () => {
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Track mobile landing page view for Reddit
    trackRedditViewContent('mobile_landing_page', { 
      page: 'mobile_home',
      device: 'mobile' 
    });
    
    // Track GA page view
    trackEvent('mobile_landing_view', { 
      page: 'mobile_home',
      device: 'mobile'
    });

    // Setup intersection observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all animated elements
    const animatedElements = document.querySelectorAll('.scroll-animate');
    animatedElements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const handleCTAClick = (location: string) => {
    // Track CTA clicks for both platforms
    trackEvent('cta_click', { 
      location,
      page: 'mobile_home',
      device: 'mobile'
    });
    
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header matching desktop */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm border-b border-primary/20 shadow-lg">
        <div className="flex items-center justify-between px-4 py-2">
          <BrandLogo 
            imgClassName="h-14 w-auto" 
            linkClassName="flex items-center"
          />
          <Button 
            variant="secondary" 
            size="sm"
            onClick={() => navigate('/login')}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20 transition-all duration-300"
          >
            Login
          </Button>
        </div>
      </header>

      <main className="pt-16">
        {/* Enhanced Hero Section */}
        <section className="px-4 py-12 text-center bg-gradient-subtle">
          <div className="space-y-6 animate-fade-in">
            <Badge variant="secondary" className="mb-3 animate-scale-in">
              Not a brokerage — AI-powered platform
            </Badge>
            
            <h1 className="text-3xl font-bold leading-tight tracking-tight">
              Find the Right PEO Quickly & <span className="text-primary bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">Save Thousands Per Employee</span>
            </h1>
            
            <p className="text-muted-foreground text-base leading-relaxed max-w-sm mx-auto">
              With one simple application, The PEO App delivers multiple quotes from top providers in days, not weeks. Lower costs, better benefits, more time to focus on growth.
            </p>

            {/* Enhanced Compliance Badges */}
            <div className="flex items-center justify-center gap-6 py-4">
              <div className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <Shield className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-muted-foreground text-sm font-medium">SOC II</span>
              </div>
              <div className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <Shield className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-muted-foreground text-sm font-medium">HIPAA</span>
              </div>
              <div className="flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Shield className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-muted-foreground text-sm font-medium">Patent Pending</span>
              </div>
            </div>

            <Button 
              size="lg" 
              className="w-full h-14 text-lg font-semibold relative overflow-hidden group bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300 animate-scale-in"
              onClick={() => handleCTAClick('hero')}
            >
              <span className="relative z-10">Get Free Quotes Now</span>
              <ArrowRight className="ml-2 h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />
            </Button>
          </div>
        </section>

        {/* Enhanced Key Benefits with Card Layout */}
        <section className="px-4 py-10 scroll-animate">
          <div className="grid grid-cols-1 gap-4 stagger-animation">
            <Card className="p-6 text-center hover-lift shadow-soft hover:shadow-elevation-2 transition-all duration-300 bg-gradient-subtle border-primary/10">
              <Zap className="h-8 w-8 text-primary mx-auto mb-3 animate-pulse" />
              <div className="text-lg font-bold mb-1">Lightning Fast</div>
              <div className="text-muted-foreground text-sm">Get quotes in days, not weeks</div>
            </Card>
            
            <Card className="p-6 text-center hover-lift shadow-soft hover:shadow-elevation-2 transition-all duration-300 bg-gradient-subtle border-primary/10">
              <DollarSign className="h-8 w-8 text-primary mx-auto mb-3 animate-pulse" />
              <div className="text-lg font-bold mb-1">Average $1,200+ Savings</div>
              <div className="text-muted-foreground text-sm">Per employee per year (typical savings)</div>
            </Card>
            
            <Card className="p-6 text-center hover-lift shadow-soft hover:shadow-elevation-2 transition-all duration-300 bg-gradient-subtle border-primary/10">
              <CheckCircle className="h-8 w-8 text-primary mx-auto mb-3 animate-pulse" />
              <div className="text-lg font-bold mb-1">100% Free</div>
              <div className="text-muted-foreground text-sm">Always free, no hidden costs</div>
            </Card>
          </div>
        </section>

        {/* Enhanced How It Works with Progressive Reveal */}
        <section className="px-4 py-10 bg-muted/30 scroll-animate">
          <h2 className="text-2xl font-bold text-center mb-8 animate-fade-in">How It Works</h2>
          
          <div className="space-y-6 max-w-md mx-auto">
            <Card className="p-5 hover-lift transition-all duration-300 shadow-soft hover:shadow-elevation-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-base mb-2">Tell Us About Your Business</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">One-stop application and secure document upload — everything in one place</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 hover-lift transition-all duration-300 shadow-soft hover:shadow-elevation-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-base mb-2">Get Matched Instantly</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">Our AI finds the perfect PEO providers for your specific needs</p>
                </div>
              </div>
            </Card>

            <Card className="p-5 hover-lift transition-all duration-300 shadow-soft hover:shadow-elevation-2 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-md">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-base mb-2">Compare & Choose</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">Review detailed quotes and pick the best fit for your business</p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Enhanced Sticky Bottom CTA */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-primary/10 z-40 shadow-elevation-2">
          <Button 
            size="lg" 
            className="w-full h-14 text-lg font-semibold relative overflow-hidden group bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => handleCTAClick('sticky')}
          >
            <span className="relative z-10">Get Free PEO Quotes</span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />
          </Button>
        </div>

        {/* Add bottom padding to account for sticky CTA */}
        <div className="h-24"></div>
      </main>
    </div>
  );
};